import { useState, useRef } from 'react';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay
} from '@dnd-kit/core';
import { GripVertical, Trash2, Plus } from 'lucide-react';

/* =========================
   ITEM DRAGGABLE MANUAL
========================= */
function PipelineItem({ 
  column, 
  onNameChange, 
  onRemove,
  isDragging = false,
  dragListeners = null,
  dragAttributes = null,
  dragRef = null
}) {
  return (
    <div
      ref={dragRef}
      {...dragAttributes}
      {...dragListeners}
      className={`flex items-center gap-3 bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 transition-all ${
        isDragging ? 'shadow-lg opacity-60' : 'hover:bg-slate-100'
      }`}
    >
      {/* HANDLE */}
      <button
        className="cursor-grab text-slate-400 hover:text-slate-600 active:cursor-grabbing"
        type="button"
        aria-label="Arrastrar para reordenar"
      >
        <GripVertical size={16} />
      </button>

      {/* NOMBRE */}
      <input
        value={column.nombre}
        onChange={e => onNameChange(column.id, e.target.value)}
        className="flex-1 bg-transparent outline-none text-sm font-medium min-w-0"
        placeholder="Nombre del embudo"
        disabled={isDragging}
      />

      {/* ELIMINAR */}
      <button
        onClick={() => !isDragging && onRemove(column.id)}
        className={`text-red-500 hover:text-red-700 transition-colors ${
          isDragging ? 'opacity-50 cursor-not-allowed' : ''
        }`}
        type="button"
        aria-label="Eliminar embudo"
        disabled={isDragging}
      >
        <Trash2 size={14} />
      </button>
    </div>
  );
}

/* =========================
   MODAL PRINCIPAL - VERSIÓN SIMPLIFICADA
========================= */
export default function PipelineEditorModal({ pipeline, onClose, onSave }) {
  const [columns, setColumns] = useState(() => {
    if (!pipeline || !Array.isArray(pipeline)) return [];
    
    return pipeline
      .slice()
      .sort((a, b) => (a.orden || 0) - (b.orden || 0))
      .map(p => ({
        id: p.pipeline_id || p.id,
        nombre: p.nombre || p.pipeline || 'Sin nombre',
        orden: p.orden || 0,
        isNew: false,
        deleted: false
      }));
  });

  const [activeId, setActiveId] = useState(null);
  const dragStartPos = useRef({ y: 0, index: 0 });

  /* ===== SENSORES SIMPLIFICADOS ===== */
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 1, // Mínima distancia para activar
      }
    })
  );

  /* ===== HANDLE DRAG START ===== */
  const handleDragStart = (event) => {
    const { active } = event;
    setActiveId(active.id);
    
    // Guardar posición inicial
    const visibleColumns = columns.filter(c => !c.deleted);
    const startIndex = visibleColumns.findIndex(c => c.id === active.id);
    dragStartPos.current = {
      y: event.activatorEvent.clientY,
      index: startIndex
    };
    
    console.log('🚀 Drag start:', active.id, 'index:', startIndex);
  };

  /* ===== HANDLE DRAG OVER ===== */
  const handleDragOver = (event) => {
    if (!activeId) return;
    
    const { over } = event;
    if (!over || activeId === over.id) return;

    const visibleColumns = columns.filter(c => !c.deleted);
    const oldIndex = visibleColumns.findIndex(c => c.id === activeId);
    const newIndex = visibleColumns.findIndex(c => c.id === over.id);

    if (oldIndex === -1 || newIndex === -1) return;

    // Solo actualizar si el índice cambió
    if (oldIndex !== newIndex) {
      // Reordenar manualmente
      const newColumns = [...visibleColumns];
      const [movedColumn] = newColumns.splice(oldIndex, 1);
      newColumns.splice(newIndex, 0, movedColumn);
      
      // Actualizar órdenes
      const reorderedWithOrder = newColumns.map((col, index) => ({
        ...col,
        orden: index + 1
      }));

      // Actualizar estado
      setColumns(prev => {
        const deletedColumns = prev.filter(c => c.deleted);
        return [...reorderedWithOrder, ...deletedColumns];
      });
    }
  };

  /* ===== HANDLE DRAG END ===== */
  const handleDragEnd = (event) => {
    const { over } = event;
    
    console.log('🎯 Drag end:', { activeId, over: over?.id });
    
    // Resetear estado de drag
    setActiveId(null);
    
    // Si se soltó sobre un área válida, ya se actualizó en handleDragOver
    // Solo necesitamos asegurar que el orden esté actualizado
    if (over) {
      console.log('✅ Reordenamiento completado');
    }
  };

  /* ===== HANDLE DRAG CANCEL ===== */
  const handleDragCancel = () => {
    console.log('❌ Drag cancelado');
    setActiveId(null);
  };

  /* ===== CRUD UI ===== */
  const addColumn = () => {
    const visibleColumns = columns.filter(c => !c.deleted);
    const newOrden = visibleColumns.length + 1;
    
    setColumns(cols => [
      ...cols,
      {
        id: crypto.randomUUID ? crypto.randomUUID() : `temp-${Date.now()}`,
        nombre: 'Nuevo embudo',
        orden: newOrden,
        isNew: true,
        deleted: false
      }
    ]);
  };

  const updateName = (id, nombre) => {
    setColumns(cols =>
      cols.map(c => (c.id === id ? { ...c, nombre } : c))
    );
  };

  const removeColumn = (id) => {
    if (!window.confirm('¿Eliminar este embudo?\nLos leads asignados se moverán al primer embudo disponible.')) return;
    
    setColumns(cols =>
      cols.map(c =>
        c.id === id ? { ...c, deleted: true } : c
      )
    );
  };

  /* ===== GUARDAR ===== */
  const handleSave = () => {
    const activos = columns.filter(c => !c.deleted);
    const toDelete = columns.filter(c => c.deleted && !c.isNew);

    // Validaciones
    if (activos.length === 0) {
      alert('Debe haber al menos un embudo activo');
      return;
    }

    // Validar nombres
    const emptyNames = activos.filter(c => !c.nombre.trim());
    if (emptyNames.length > 0) {
      alert('Todos los embudos deben tener un nombre válido');
      return;
    }

    // Validar duplicados
    const nombres = activos.map(c => c.nombre.trim().toLowerCase());
    const nombresUnicos = new Set(nombres);
    if (nombresUnicos.size !== nombres.length) {
      alert('No puede haber embudos con el mismo nombre');
      return;
    }

    // Preparar payload
    const payload = {
      crear: activos.filter(c => c.isNew).map(c => ({
        nombre: c.nombre.trim(),
        orden: c.orden
      })),
      editar: activos.filter(c => !c.isNew).map(c => ({
        id: c.id,
        nombre: c.nombre.trim(),
        orden: c.orden
      })),
      eliminar: toDelete.map(c => c.id),
      reordenar: activos.map(c => ({ id: c.id, orden: c.orden }))
    };

    console.log('📤 Payload para guardar:', payload);
    onSave(payload);
  };

  const visibleColumns = columns.filter(c => !c.deleted);
  const activeColumn = activeId ? visibleColumns.find(c => c.id === activeId) : null;

  return (
    <div 
      className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="bg-white w-full max-w-xl rounded-2xl p-6 shadow-xl max-h-[90vh] flex flex-col">
        <div className="mb-6">
          <h2 className="text-xl font-bold text-slate-800">Gestionar embudos</h2>
          <p className="text-slate-500 text-sm mt-1">
            Arrastra los embudos usando el ícono <GripVertical size={12} className="inline" /> para reordenarlos
          </p>
        </div>

        <div className="flex-1 overflow-y-auto pr-2">
          {visibleColumns.length === 0 ? (
            <div className="text-center py-8 text-slate-400">
              <p>No hay embudos activos</p>
              <p className="text-sm mt-1">Agrega uno nuevo para comenzar</p>
            </div>
          ) : (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragStart={handleDragStart}
              onDragOver={handleDragOver}
              onDragEnd={handleDragEnd}
              onDragCancel={handleDragCancel}
              autoScroll={false}
            >
              <div className="space-y-3">
                {visibleColumns.map((col) => (
                  <div
                    key={col.id}
                    data-id={col.id}
                    className={col.id === activeId ? 'opacity-30' : ''}
                  >
                    <PipelineItem
                      column={col}
                      onNameChange={updateName}
                      onRemove={removeColumn}
                      isDragging={col.id === activeId}
                    />
                  </div>
                ))}
              </div>

              <DragOverlay>
                {activeColumn && (
                  <div className="rotate-3">
                    <PipelineItem
                      column={activeColumn}
                      onNameChange={updateName}
                      onRemove={removeColumn}
                      isDragging={true}
                    />
                  </div>
                )}
              </DragOverlay>
            </DndContext>
          )}

          {/* Indicador de columnas eliminadas */}
          {columns.filter(c => c.deleted).length > 0 && (
            <div className="mt-4 pt-4 border-t border-slate-200">
              <p className="text-xs text-slate-400 mb-2">
                {columns.filter(c => c.deleted).length} embudo(s) marcado(s) para eliminar
              </p>
            </div>
          )}
        </div>

        {/* FOOTER */}
        <div className="pt-6 mt-6 border-t border-slate-200">
          <div className="flex justify-between items-center">
            <button
              onClick={addColumn}
              className="flex items-center gap-2 text-sm font-semibold text-blue-600 hover:text-blue-700 transition-colors"
              type="button"
            >
              <Plus size={16} />
              Añadir embudo
            </button>

            <div className="flex gap-3">
              <button
                onClick={onClose}
                className="px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
                type="button"
              >
                Cancelar
              </button>
              <button
                onClick={handleSave}
                className="px-6 py-2 rounded-lg bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                type="button"
                disabled={visibleColumns.length === 0}
              >
                Guardar cambios
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
